import {
  EMPTY_PLATFORM_SETTINGS,
  activeFinishOf,
  activeThemeOf,
  factoryChoiceOf,
  factoryThemeOf,
  finishCss,
  presentationOf,
  platformCssVars,
  themeCss,
  type FinishOptions,
  type PlatformSettings,
  type StyleChoice,
  type Presentation,
  type ThemeFile,
} from '@base/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect } from 'react';
import { api } from './api.ts';
import { setSheet } from './css-sheet.ts';
import { useMe } from './auth.tsx';
import defaultLogoUrl from './assets/beadev-logo.png';
import defaultSymbolUrl from './assets/beadev-simbolo.png';
import visual from '../../../visual.json';
import { SISTEMA_NOME } from './sistema.ts';
import { shellOf, type ShellChoice } from './shell/choice.ts';

export const PLATFORM_KEY = ['platform', 'settings'];

/**
 * O visual que o sistema traz de fábrica: o visual.json da raiz do repositório, que o Smart Builder
 * preenche com o que o cliente escolheu: o estilo pronto pelo nome (`estilo`, ADR 0126) ou, nos
 * sistemas antigos, o tema copiado (`tema`, ADR 0117), mais o logo. Vazio (a base), vale o beadev.
 */
const built = visual as {
  estilo?: unknown;
  tema?: unknown;
  logo?: unknown;
  apresentacao?: unknown;
};
export const FACTORY_THEME: ThemeFile = factoryThemeOf(built, SISTEMA_NOME);
/** O estilo de fábrica (para a Configuração › Estilo abrir nele), ou null. */
export const FACTORY_STYLE: StyleChoice | null = factoryChoiceOf(built);

/**
 * O tema do sistema sem o montado do zero: o estilo escolhido na Configuração › Estilo ou, sem
 * ele, o de fábrica. É o ponto de partida do Guia visual e o "Voltar ao padrão" dele. Muda quando a
 * aparência gravada chega (`PlatformTheme`); por isso é `let` (as telas leem o valor atual).
 */
export let BASE_THEME: ThemeFile = FACTORY_THEME;
const builtLogo =
  typeof built.logo === 'string' && /^data:image\/(png|jpeg|webp);base64,/.test(built.logo)
    ? built.logo
    : null;
/** O logo do próprio cliente, sem cair no do beadev (a página de apresentação usa o nome no lugar). */
export const BUILT_LOGO_URL: string | null = builtLogo;
export const DEFAULT_LOGO_URL = builtLogo ?? defaultLogoUrl;
export const DEFAULT_SYMBOL_URL = builtLogo ?? defaultSymbolUrl;
/** A página de apresentação de fábrica (ADR 0118), ou null: sem ela, o endereço abre a tela de entrar. */
export const PRESENTATION: Presentation | null = presentationOf(built.apresentacao);

/** Aparência do sistema (Configuração › Plataforma). Enquanto carrega, vale o padrão da marca. */
export function usePlatformSettings() {
  const loggedIn = !!useMe().data;
  const query = useQuery({
    queryKey: PLATFORM_KEY,
    enabled: loggedIn,
    queryFn: () => api.get<PlatformSettings>('/platform/settings'),
    staleTime: 5 * 60_000,
  });
  return query;
}

/**
 * Mantém o `BASE_THEME` igual ao estilo gravado assim que a aparência chega ou muda no cache,
 * antes de as telas renderizarem (o Guia visual abre nele). Ligado uma vez, em main.tsx.
 */
export function watchPlatformSettings(client: QueryClient) {
  const sync = () => {
    const data = client.getQueryData<PlatformSettings>(PLATFORM_KEY);
    BASE_THEME = data ? baseThemeOf(data) : FACTORY_THEME;
  };
  sync();
  return client.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === PLATFORM_KEY[0] && event.query.queryKey[1] === PLATFORM_KEY[1])
      sync();
  });
}

/** O tema do sistema sem o montado do zero: o estilo escolhido ou o de fábrica (ADR 0126). */
function baseThemeOf(s: Pick<PlatformSettings, 'style'>): ThemeFile {
  return activeThemeOf({ theme: null, style: s.style }, FACTORY_THEME, SISTEMA_NOME);
}

/** Logo, símbolo e nome que a moldura mostra: o configurado ou o padrão da marca. */
export function usePlatformLook() {
  const s = usePlatformSettings().data ?? EMPTY_PLATFORM_SETTINGS;
  return {
    logoUrl: s.logo ?? DEFAULT_LOGO_URL,
    symbolUrl: s.symbol ?? DEFAULT_SYMBOL_URL,
    appName: s.appName ?? SISTEMA_NOME,
  };
}

/** O tema que vale agora (ADR 0126): o do zero, o estilo escolhido ou o de fábrica. */
export function useActiveTheme(): ThemeFile {
  return activeThemeOf(usePlatformSettings().data, FACTORY_THEME, SISTEMA_NOME);
}

/** A moldura que vale agora (ADR 0128): o layout do estilo e o menu no celular. */
export function useActiveShell(): ShellChoice {
  const settings = usePlatformSettings().data;
  const menu = activeThemeOf(settings, FACTORY_THEME, SISTEMA_NOME).menu;
  return shellOf(settings, FACTORY_STYLE, menu);
}

/** Acabamento que uma prévia põe por cima do que vale (o estúdio mostra o que está sendo escolhido). */
export const FinishPreviewContext = createContext<FinishOptions | null>(null);

/** O acabamento que vale agora (ADR 0128): os padrões de tela leem o vazio, o gráfico e o documento. */
export function useActiveFinish(): FinishOptions {
  const preview = useContext(FinishPreviewContext);
  const active = activeFinishOf(usePlatformSettings().data, FACTORY_STYLE);
  return preview ?? active;
}

/** Variáveis que a aparência configurada trocou (para devolver o padrão quando mudar de novo). */
let applied: string[] = [];

/** Aplica as cores da plataforma nas variáveis do styles.css (o tema inteiro segue). */
export function applyPlatformColors(
  s: Pick<PlatformSettings, 'accentColor' | 'paperColor' | 'inkColor'>,
) {
  const style = document.documentElement.style;
  for (const name of applied) style.removeProperty(name);
  const vars = platformCssVars(s);
  for (const [name, value] of Object.entries(vars)) style.setProperty(name, value);
  applied = Object.keys(vars);
}

/**
 * Aplica o tema do Guia visual (cores do claro e do escuro, medidas e estilo) numa folha de estilo
 * própria (construída pelo JavaScript, que o CSP de produção aceita: veja css-sheet.ts), por cima do styles.css. As três cores da Plataforma, quando escolhidas, ficam por cima
 * dele (applyPlatformColors). Sem tema gravado, vale o que o sistema traz (BASE_THEME). Também roda
 * ao abrir, antes de entrar: a tela de login já sai com o visual do sistema.
 */
export function applyTheme(theme: ThemeFile | null, finish?: FinishOptions) {
  const t = theme ?? BASE_THEME;
  setSheet('base-theme', themeCss(t));
  // O acabamento depois do tema: as variáveis dele (painel, botão secundário) valem por cima.
  const fallback = theme
    ? activeFinishOf({ theme, style: null }, null)
    : activeFinishOf(null, FACTORY_STYLE);
  setSheet('base-finish', finishCss(finish ?? fallback));
}

/** Liga a aparência gravada à moldura das telas logadas. */
export function PlatformTheme() {
  const data = usePlatformSettings().data;
  useEffect(() => {
    if (!data) return;
    applyTheme(data.theme, activeFinishOf(data, FACTORY_STYLE));
    applyPlatformColors(data);
  }, [data]);
  return null;
}
